<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="clockDirty.id ? 'Edit Marketing Clock' : 'Add New Marketing Clock'"
  >
    <v-card-text class="pa-6">
      <validation-observer ref="validation">
        <v-row
          no-gutters
          class="mb-2"
          align="center"
        >
          <v-col class="font-weight-bold">
            Select marketing campaign
          </v-col>
          <v-col
            cols="auto"
            class="px-3 pb-1"
          >
            <v-switch
              v-model="playlistsIncludeExpired"
              dense
              class="ma-0"
              hide-details
              @change="fetchPlaylists()"
            >
              <template
                v-slot:label
              >
                <span class="text-caption">
                  Include Expired
                </span>
              </template>
            </v-switch>
          </v-col>
          <v-col cols="auto">
            <v-btn
              small
              color="secondary"
              text
              @click="editPlaylist.playlist = undefined; editPlaylist.visible = true"
            >
              <v-icon
                left
                small
              >
                fa fa-plus
              </v-icon>
              New Marketing
            </v-btn>
          </v-col>
        </v-row>

        <v-card
          outlined
          flat
        >
          <div
            v-if="!playlists.data.length"
            class="text-center py-4 grey--text"
          >
            <span v-if="radio.client">No marketing campaigns for client {{ radio.client.name }}</span>
            <span v-else>No marketing campaigns found</span>
          </div>
          <v-list
            class="ma-0 pa-0"
          >
            <v-list-item-group
              v-model="selectedPlaylistId"
              color="primary"
            >
              <v-list-item
                v-for="playlist in playlists.data"
                :key="playlist.id"
                :value="playlist.id"
                class="no-gutters align-start list-element--bordered py-1"
              >
                <v-col>
                  <div>
                    {{ playlist.title }}
                  </div>
                  <div class="grey--text caption">
                    {{ playlist.description }}
                  </div>
                </v-col>
                <v-col
                  cols="auto"
                  class="px-3 caption"
                />
                <v-col
                  cols="auto"
                  class="caption"
                >
                  <span
                    v-if="playlist.is_active"
                    class="success--text"
                  >
                    Active
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Inactive
                  </span>
                  &middot;
                  <date-range-label
                    :from="playlist.active_from"
                    :until="playlist.active_until"
                  />
                </v-col>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>

        <v-row
          no-gutters
          class="mb-2 mt-6"
        >
          <v-col class="font-weight-bold">
            Advertisements
          </v-col>
          <v-col cols="auto">
            <v-btn
              small
              color="secondary"
              text
              :disabled="!selectedPlaylist"
              @click="editPlaylist.playlist = selectedPlaylist; editPlaylist.visible = true"
            >
              <v-icon
                left
                small
              >
                fa fa-edit
              </v-icon>
              Edit Marketing
            </v-btn>
          </v-col>
        </v-row>
        <v-card
          outlined
          flat
        >
          <div
            v-if="!selectedPlaylist"
            class="text-center py-4 grey--text"
          >
            Select a campaign above first
          </div>
          <v-list
            v-else
            class="ma-0 pa-0"
          >
            <v-list-item
              v-for="file in selectedPlaylist.library_files"
              :key="file.id"
              class="list-element--bordered"
            >
              <v-list-item-title>
                {{ file.original_file }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
        <div class="mb-2 mt-6 font-weight-bold">
          On weekdays
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-btn-toggle
            v-model="clockDirty.days"
            multiple
            dense
            class="row no-gutters mb-2"
            color="primary"
            style="width: 100%"
          >
            <v-btn
              v-for="day in days"
              :key="day.day"
              :value="day.day"
              large
              class="col caption text-none"
            >
              {{ day.name }}
            </v-btn>
          </v-btn-toggle>
          <div class="v-text-field__details mb-2">
            <div class="v-messages theme--light error--text">
              <div class="v-messages__wrapper">
                {{ errors[0] }}
              </div>
            </div>
          </div>
        </validation-provider>

        <div class="font-weight-bold mb-2">
          At time in day
        </div>
        <v-row no-gutters>
          <v-col class="pr-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required|time"
            >
              <v-text-field
                v-model="clockDirty.start_time_at"
                v-mask="'##:##'"
                type="tel"
                outlined
                clearable
                label="From"
                placeholder="00:00"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col class="pl-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required|time"
            >
              <v-text-field
                v-model="clockDirty.end_time_at"
                v-mask="'##:##'"
                type="tel"
                outlined
                clearable
                label="To"
                placeholder="23:59"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-row align="center">
          <v-col class="font-weight-bold">
            Playback Type
          </v-col>
          <v-col cols="6">
            <v-btn-toggle
              v-model="clockDirty.playback_type"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="PLAYBACK_TYPE_AFTER_MINUTES"
                large
                class="col text-none"
              >
                After Minutes
              </v-btn>
              <v-btn
                :value="PLAYBACK_TYPE_AFTER_SONGS"
                large
                class="col text-none"
              >
                After Songs
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="clockDirty.playback_type === PLAYBACK_TYPE_AFTER_SONGS || clockDirty.playback_type === PLAYBACK_TYPE_AFTER_MINUTES">
          <div class="font-weight-bold mb-2 mt-4">
            Frequency
          </div>
          <v-row align="center">
            <v-col cols="auto">
              Play
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required|numeric"
              >
                <v-text-field
                  v-model="clockDirty.playback_content_count"
                  outlined
                  type="number"
                  dense
                  hide-details
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col cols="auto">
              advertisements <strong>after</strong>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required|numeric"
              >
                <v-text-field
                  v-if="clockDirty.playback_type === PLAYBACK_TYPE_AFTER_SONGS"
                  v-model="clockDirty.playback_every_songs"
                  outlined
                  type="number"
                  dense
                  hide-details
                  :error-messages="errors"
                />
                <v-text-field
                  v-else-if="clockDirty.playback_type === PLAYBACK_TYPE_AFTER_MINUTES"
                  v-model="clockDirty.playback_every_minutes"
                  outlined
                  type="number"
                  dense
                  hide-details
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <v-col cols="auto">
              {{ clockDirty.playback_type === PLAYBACK_TYPE_AFTER_MINUTES ? 'mins of playback' : 'music songs' }}
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="clockDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteClock()"
    />

    <edit-marketing-campaign-dialog
      :visible.sync="editPlaylist.visible"
      :playlist="editPlaylist.playlist"
      :client="radio.client"
      @playlist:added="fetchPlaylists(); selectedPlaylistId = $event.id"
      @playlist:updated="fetchPlaylists()"
      @playlist:deleted="fetchPlaylists()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import ClockModel from '@/models/ClockModel'
import Playlists from '@/services/Playlists'
import CardDialog from '@/components/helpers/CardDialog'
import Radios from '@/services/Radios'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import EditMarketingCampaignDialog from '@/components/marketing/EditMarketingCampaignDialog'
import DateRangeLabel from '@/components/helpers/DateRangeLabel'

export default {
  name: 'EditMarketingClockDialog',
  components: { DateRangeLabel, EditMarketingCampaignDialog, ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    clock: {
      type: Object,
      default: () => cloneDeep(ClockModel.emptyModel)
    },
    radio: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      clockDirty: cloneDeep(this.clock),
      playlistsIncludeExpired: false,
      playlists: {
        loading: false,
        data: []
      },
      selectedPlaylistId: undefined,
      editPlaylist: {
        playlist: undefined,
        visible: false
      },
      submitting: false,
      deleting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedPlaylist() {
      if (!this.selectedPlaylistId) return undefined

      return this.playlists.data.find(p => p.id === this.selectedPlaylistId)
    }
  },
  watch: {
    visible(newValue) {
      if (!newValue) return

      this.clockDirty = cloneDeep(this.clock)
      if (this.$refs.validation) this.$refs.validation.reset()

      if (this.clock.id) {
        this.selectedPlaylistId = this.clock.playlists[0].id
      }
    }
  },
  created() {
    this.days = ClockModel.days
    this.PLAYBACK_TYPE_DEFAULT = ClockModel.PLAYBACK_TYPE_DEFAULT
    this.PLAYBACK_TYPE_AFTER_SONGS = ClockModel.PLAYBACK_TYPE_AFTER_SONGS
    this.PLAYBACK_TYPE_AFTER_MINUTES = ClockModel.PLAYBACK_TYPE_AFTER_MINUTES

    this.fetchPlaylists()
  },
  methods: {
    async fetchPlaylists() {
      try {
        this.playlists.loading = true

        this.playlists.data = await Playlists.fetch({
          content_type: ClockModel.CONTENT_TYPE_MARKETING,
          client_id: this.radio.client ? this.radio.client.id : undefined,
          include_expired: this.playlistsIncludeExpired
        })
      } catch (e) {

      } finally {
        this.playlists.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      this.submitting = true

      try {
        const data = {
          content_type: ClockModel.CONTENT_TYPE_MARKETING,
          playlists: [this.selectedPlaylistId],
          start_time_at: this.$dayjs(this.clockDirty.start_time_at, 'HH:mm').format('HH:mm:ss'),
          end_time_at: this.$dayjs(this.clockDirty.end_time_at, 'HH:mm').format('HH:mm:ss'),
          days: this.clockDirty.days,
          playback_type: this.clockDirty.playback_type
        }

        if (data.playback_type === ClockModel.PLAYBACK_TYPE_AFTER_SONGS) {
          data.playback_every_songs = this.clockDirty.playback_every_songs
          data.playback_content_count = this.clockDirty.playback_content_count
        } else if (data.playback_type === ClockModel.PLAYBACK_TYPE_AFTER_MINUTES) {
          data.playback_every_minutes = this.clockDirty.playback_every_minutes
          data.playback_content_count = this.clockDirty.playback_content_count
        }

        if (this.clockDirty.id) {
          const result = await Radios.updateClock(this.radio.id, this.clockDirty.id, data)

          this.$emit('clock:updated', result)

          this.$store.dispatch('alert', {
            text: 'Clock updated',
            color: 'success'
          })
        } else {
          const result = await Radios.storeClock(this.radio.id, data)

          this.$emit('clock:added', result)

          this.$store.dispatch('alert', {
            text: 'New clock successfully added',
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting clock',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteClock() {
      this.deleting = true

      try {
        const result = await Radios.deleteClock(this.radio.id, this.clockDirty.id)

        this.$store.dispatch('alert', {
          text: 'Clock deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('clock:deleted', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    }
  }
}
</script>
